import { useMemo, useRef, useState } from 'react';
import {
  Bouton, IconeCorbeille, IconePlus, SaisieDifferee, useColonnes,
} from '@journal/ui';

/**
 * Cahier journal du jour, en tableau.
 *
 * ── Aligné sur l'application d'origine, geste pour geste ────────────────────
 * Sept colonnes, comme `.jnl-table` : Horaire · Domaine / Sous-domaine ·
 * Objectifs · Déroulement de l'activité · Matériel · Duplication · corbeille.
 * Les deux dernières ne s'impriment pas : elles servent à agir, pas à lire.
 *
 * Seul ce que la v1 affiche ET enregistre est repris. Ce que la v2 avait ajouté
 * a été retiré à la demande : la case « Traité » devant un objectif (la v1 en
 * a la fonction, jamais affichée), le bouton « Détails » avec séquence et
 * compétences (des colonnes de sa base qu'aucun écran ne remplit), le champ
 * « Objectif de la séance » et la mention « À préparer ».
 *
 * ── Tout se saisit EN PLACE ─────────────────────────────────────────────────
 * Préparer une journée, c'est remplir dix cases à la suite sans quitter le
 * clavier. Même l'objectif libre s'écrit dans la case, sous les objectifs
 * existants : Entrée l'ajoute, comme quitter le champ.
 *
 * ── Pourquoi un tableau et non des cartes ───────────────────────────────────
 * Une journée se lit en BALAYANT une colonne : « où manque le matériel ? ».
 * C'est aussi ce qui s'imprime, le document qu'on pose devant un inspecteur.
 */

/**
 * Ce qu'affiche une colonne héritée du créneau.
 *
 * 1. la saisie de la SÉANCE — ce que l'enseignant a écrit aujourd'hui ;
 * 2. la saisie libre du CRÉNEAU — ce qu'il a écrit dans son emploi du temps ;
 * 3. la valeur résolue depuis le PROGRAMME officiel.
 *
 * La chaîne vide compte pour « rien » : un champ effacé laisse l'héritage
 * reprendre, sinon effacer une valeur reviendrait à la figer à vide.
 */
export function valeurHeritee(
  seance: string | undefined,
  creneau: string,
  programme = '',
): string {
  return seance || creneau || programme;
}

export interface ObjectifLigne {
  id: string;
  text: string;
}

export interface LigneJournal {
  /** Repère d'une ligne ajoutée avec « Ajouter » : son identité à l'écran. */
  cle?: string;
  slotId: string | null;
  /** Domaine saisi librement DANS LE CRÉNEAU. Voir `valeurHeritee`. */
  slotSubjectText: string;
  /** Sous-domaine saisi librement dans le créneau. */
  slotSubjectAreaText: string;
  startsAt: string | null;
  endsAt: string | null;
  subjectId: string | null;
  subjectName: string;
  color: string;
  lesson: {
    id: string;
    procedure: string;
    materials: string;
    subjectText: string;
    subjectAreaText: string;
    duplicated: boolean;
    objectives: ObjectifLigne[];
  } | null;
  /** Progression prévue cette semaine pour la matière : l'aperçu de la case Objectifs. */
  suggestion?: { sequence: string; content: string; competencies: string } | null;
}

/** Les champs d'une séance qui se saisissent dans le tableau. */
export type ChampLigne =
  | 'procedure' | 'materials' | 'subjectText' | 'subjectAreaText' | 'startsAt' | 'endsAt';

/** Les deux sources des boutons « Récupérer » de la v1. */
export type SourceRecuperation = 'progression' | 'programme';

/** Le domaine affiché sur la ligne : c'est lui qui filtre les deux sélecteurs. */
export function domaineDeLaLigne(ligne: LigneJournal): string {
  return valeurHeritee(ligne.lesson?.subjectText, ligne.slotSubjectText, ligne.subjectName);
}

/** « 08:30 » → « 8h30 », « 09:00 » → « 9h ». Format de l'original. */
export function heureCourte(h: string | null): string {
  if (!h) return '';
  const [hh, mm] = h.split(':');
  return `${Number(hh)}h${mm === '00' ? '' : mm}`;
}

/*
 * Générique sur la ligne : l'écran a son propre type, plus riche, et le
 * dupliquer ici en ferait deux définitions à tenir d'accord. Le composant ne
 * demande que ce qu'il affiche.
 */
export function TableauJournal<L extends LigneJournal>({
  lignes, surChamp, surObjectif, surSuppressionObjectif, surObjectifLibre,
  surRecuperation, surAjoutPrevu, surDuplication, surSuppression, surAjoutLigne,
  domaines = [], sousDomaines = {},
}: {
  lignes: L[];
  surChamp: (ligne: L, champ: ChampLigne, valeur: string) => Promise<boolean>;
  surObjectif: (objectifId: string, texte: string) => Promise<boolean>;
  surSuppressionObjectif: (objectif: ObjectifLigne) => void;
  /** Ajoute un objectif écrit à la main. Renvoie `false` si rien n'est parti. */
  surObjectifLibre: (ligne: L, texte: string) => Promise<boolean>;
  /** Ouvre l'un des deux sélecteurs « Récupérer ». */
  surRecuperation: (ligne: L, source: SourceRecuperation) => void;
  /** « Ajouter cette séance au journal », sous l'aperçu de la progression prévue. */
  surAjoutPrevu?: (ligne: L) => void;
  /** La case « Duplication » : `coche` dit l'état voulu. */
  surDuplication: (ligne: L, coche: boolean) => void;
  surSuppression: (ligne: L) => void;
  /** « + Ajouter une ligne », sous le tableau. */
  surAjoutLigne: () => void;
  /** Matières proposées à la saisie du domaine. */
  domaines?: string[];
  /** Sous-domaines proposés, par nom de matière. */
  sousDomaines?: Record<string, string[]>;
}) {
  // Largeurs réglables et mémorisées, comme `ColResize` de l'original.
  const colonnes = useColonnes('journal');
  /*
   * Repli quand aucune matière n'est encore saisie : proposer TOUS les
   * sous-domaines vaut mieux qu'une liste vide.
   */
  const tousSousDomaines = useMemo(
    () => [...new Set(Object.values(sousDomaines).flat())].sort((a, b) => a.localeCompare(b, 'fr')),
    [sousDomaines],
  );

  const enTete = 'border-b border-[var(--color-border)] bg-[var(--color-surface-raised)] px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]';

  return (
    <div className="flex flex-col">
      <div
        role="region"
        aria-label="Cahier journal du jour"
        // Ce fragment est ce qui part à l'imprimante : le bouton Imprimer le
        // cherche dans la page, le copie, et remplace ses champs par du texte.
        data-impression="journal"
        tabIndex={0}
        /*
         * `relative` : la zone qui défile est le repère de TOUT ce qui est
         * positionné dedans. Sans lui, un texte réservé aux lecteurs d'écran —
         * l'en-tête de la colonne corbeille, la légende du tableau — se plaçait
         * par rapport à la page, sortait de la zone, et élargissait la page
         * entière : deux barres de défilement en bas de l'écran, l'une pour le
         * tableau, l'autre pour la page.
         */
        className="impression-paysage relative overflow-x-auto focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
      >
        {/*
          * `tableau-cartes` : sous 640 px, le tableau s'empile en cartes. Les
          * rôles ARIA sont redonnés, le `display: block` les retirant.
          */}
        <table role="table" className="tableau-cartes w-full border-collapse text-sm">
          <caption className="sr-only">
            Séances de la journée : horaire, domaine, objectifs, déroulement et
            matériel.
          </caption>
          {colonnes.colgroup([
            'Horaire', 'Domaine / Sous-domaine', 'Objectifs',
            "Déroulement de l'activité", 'Matériel', 'Duplication', 'Suppression',
          ])}
          <thead>
            <tr>
              {[
                ['Horaire', '6.25rem'],
                ['Domaine / Sous-domaine', '9rem'],
                ['Objectifs', ''],
                ["Déroulement de l'activité", ''],
                ['Matériel', '7rem'],
              ].map(([titre, largeur]) => (
                <th
                  key={titre}
                  scope="col"
                  // La largeur RÉGLÉE l'emporte sur celle du gabarit.
                  style={{
                    ...(largeur ? { width: largeur } : {}),
                    ...colonnes.entete(titre as string).style,
                  }}
                  className={`relative ${enTete}`}
                >
                  {titre}
                  {colonnes.poignee(titre as string)}
                </th>
              ))}
              <th
                scope="col"
                style={{ width: '5.75rem' }}
                title="Recopier la séance sur les semaines suivantes de l'année"
                className={`${enTete} text-center print:hidden`}
              >
                Duplication
              </th>
              <th scope="col" style={{ width: '2.5rem' }} className={`relative ${enTete} print:hidden`}>
                <span className="sr-only">Suppression</span>
              </th>
            </tr>
          </thead>

          <tbody role="rowgroup">
            {lignes.map((ligne, index) => {
              const seance = ligne.lesson;
              const repere = heureCourte(ligne.startsAt) || `ligne ${index + 1}`;

              return (
                <tr
                  /*
                   * Le repère d'une ligne ajoutée passe en premier : quand sa
                   * séance revient du serveur, React garde le même élément, et
                   * le curseur reste dans le champ qu'on remplit.
                   */
                  key={ligne.cle ?? ligne.slotId ?? seance?.id ?? `${ligne.startsAt}-${index}`}
                  data-cle={ligne.cle}
                  role="row"
                  className="border-t border-[var(--color-border)] align-top"
                  // Liseré de matière, comme l'original.
                  style={{ borderLeft: `4px solid ${ligne.color}` }}
                >
                  <td role="cell" data-libelle="Horaire" className="px-2.5 py-2 text-[13px] tabular-nums">
                    {/* Le jumeau lisible part à l'impression, les champs non. */}
                    <span className="impression-texte impression-seule hidden">
                      {ligne.startsAt
                        ? `${heureCourte(ligne.startsAt)} – ${heureCourte(ligne.endsAt)}`
                        : ''}
                    </span>
                    <div className="flex flex-col gap-1 print:hidden">
                      <input
                        type="time"
                        defaultValue={ligne.startsAt ?? ''}
                        aria-label={`Heure de début, ligne ${index + 1}`}
                        onChange={(e) => surChamp(ligne, 'startsAt', e.target.value)}
                        className="w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-[13px] tabular-nums hover:border-[var(--color-border)] focus:border-[var(--color-border-strong)] focus:bg-[var(--color-surface-input)] focus-visible:outline-none"
                      />
                      <input
                        type="time"
                        defaultValue={ligne.endsAt ?? ''}
                        aria-label={`Heure de fin, ligne ${index + 1}`}
                        onChange={(e) => surChamp(ligne, 'endsAt', e.target.value)}
                        className="w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-[13px] tabular-nums hover:border-[var(--color-border)] focus:border-[var(--color-border-strong)] focus:bg-[var(--color-surface-input)] focus-visible:outline-none"
                      />
                    </div>
                  </td>

                  <td role="cell" data-libelle="Domaine / Sous-domaine" className="border-l border-[var(--color-border)] p-1 align-top">
                    {/* Saisie libre avec propositions, comme l'original. */}
                    <SaisieDifferee
                      libelle={`Domaine · ${repere}`}
                      valeur={domaineDeLaLigne(ligne)}
                      surEnregistrement={(v) => surChamp(ligne, 'subjectText', v)}
                      placeholder="Domaine (saisie libre)…"
                      propositions={domaines}
                    />
                    <SaisieDifferee
                      libelle={`Sous-domaine · ${repere}`}
                      valeur={valeurHeritee(seance?.subjectAreaText, ligne.slotSubjectAreaText)}
                      surEnregistrement={(v) => surChamp(ligne, 'subjectAreaText', v)}
                      placeholder="Sous-domaine (saisie libre)…"
                      propositions={sousDomaines[domaineDeLaLigne(ligne)] ?? tousSousDomaines}
                    />
                  </td>

                  <td role="cell" data-libelle="Objectifs" className="border-l border-[var(--color-border)] p-1 align-top">
                    <div className="flex flex-col gap-1">
                      {/*
                        * « Planifié dans la progression » : la séance prévue
                        * cette semaine pour la matière, tant que la case n'a
                        * aucun objectif. Un APERÇU, rien n'est écrit : seul le
                        * bouton l'ajoute, comme dans l'original. L'imprimer
                        * ferait croire à un objectif retenu.
                        */}
                      {(seance?.objectives ?? []).length === 0 && ligne.suggestion
                        && (ligne.suggestion.sequence || ligne.suggestion.content) && surAjoutPrevu && (
                        <div
                          className="rounded-md border border-dashed p-1.5 print:hidden"
                          style={{ borderColor: ligne.color }}
                        >
                          <p className="text-[11px] text-[var(--color-fg-muted)]">Planifié dans la progression :</p>
                          <p className="text-[12px] [overflow-wrap:anywhere]">
                            {ligne.suggestion.sequence || ligne.suggestion.content}
                          </p>
                          {ligne.suggestion.competencies && (
                            <p className="mt-0.5 whitespace-pre-wrap text-[11px] text-[var(--color-fg-muted)] [overflow-wrap:anywhere]">
                              {ligne.suggestion.competencies}
                            </p>
                          )}
                          <Bouton
                            variante="secondary"
                            taille="sm"
                            onClick={() => surAjoutPrevu(ligne)}
                            aria-label={`Ajouter cette séance au journal · ${repere}`}
                            className="mt-1 w-full whitespace-normal"
                          >
                            Ajouter cette séance au journal
                          </Bouton>
                        </div>
                      )}

                      {/* Un bloc par objectif : texte modifiable, corbeille. */}
                      {(seance?.objectives ?? []).map((o) => (
                        <div
                          key={o.id}
                          className="flex items-start gap-1 rounded-md bg-[var(--color-surface-raised)] p-1"
                          style={{ borderLeft: `3px solid ${ligne.color}` }}
                        >
                          <div className="min-w-0 flex-1">
                            <SaisieDifferee
                              libelle={`Objectif · ${o.text || 'sans texte'}`}
                              valeur={o.text}
                              surEnregistrement={(v) => surObjectif(o.id, v)}
                              placeholder="Objectif…"
                            />
                          </div>
                          <Bouton
                            variante="ghost"
                            taille="sm"
                            iconeSeule
                            icone={<IconeCorbeille taille={12} />}
                            onClick={() => surSuppressionObjectif(o)}
                            aria-label={`Supprimer l'objectif « ${o.text || 'vide'} »`}
                            className="print:hidden"
                          >
                            Supprimer
                          </Bouton>
                        </div>
                      ))}

                      <div className="flex flex-col gap-1 print:hidden">
                        <ObjectifLibre
                          libelle={`Objectif libre · ${repere}`}
                          surAjout={(texte) => surObjectifLibre(ligne, texte)}
                        />
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          onClick={() => surRecuperation(ligne, 'progression')}
                          aria-label={`Récupérer depuis la progression · ${repere}`}
                          className="w-full border border-dashed whitespace-normal"
                          style={{ borderColor: ligne.color, color: ligne.color }}
                        >
                          Récupérer depuis la progression
                        </Bouton>
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          onClick={() => surRecuperation(ligne, 'programme')}
                          aria-label={`Récupérer depuis le programme de l'année · ${repere}`}
                          className="w-full border border-dashed border-[var(--color-border-strong)] whitespace-normal"
                        >
                          Récupérer depuis le programme de l'année
                        </Bouton>
                      </div>
                    </div>
                  </td>

                  <td role="cell" data-libelle="Déroulement de l'activité" className="border-l border-[var(--color-border)] p-1 align-top">
                    <SaisieDifferee
                      libelle={`Déroulement · ${repere}`}
                      valeur={seance?.procedure ?? ''}
                      surEnregistrement={(v) => surChamp(ligne, 'procedure', v)}
                      placeholder="Déroulement de la séance…"
                    />
                  </td>

                  <td role="cell" data-libelle="Matériel" className="border-l border-[var(--color-border)] p-1 align-top">
                    <SaisieDifferee
                      libelle={`Matériel · ${repere}`}
                      valeur={seance?.materials ?? ''}
                      surEnregistrement={(v) => surChamp(ligne, 'materials', v)}
                      placeholder="Matériel…"
                    />
                  </td>

                  <td role="cell" data-libelle="Duplication" className="border-l border-[var(--color-border)] p-1 text-center align-middle print:hidden">
                    <input
                      type="checkbox"
                      checked={!!seance?.duplicated}
                      onChange={(e) => surDuplication(ligne, e.target.checked)}
                      aria-label={`Dupliquer · ${repere}`}
                      title="Recopier Objectifs / Déroulement / Matériel sur le même créneau de toutes les semaines suivantes de l'année (sans écraser les séances déjà remplies)"
                      className="size-4 cursor-pointer"
                      style={{ accentColor: ligne.color }}
                    />
                  </td>

                  <td role="cell" data-libelle="Suppression" className="border-l border-[var(--color-border)] p-1 text-center align-middle print:hidden">
                    <Bouton
                      variante="ghost"
                      taille="sm"
                      iconeSeule
                      icone={<IconeCorbeille taille={13} />}
                      onClick={() => surSuppression(ligne)}
                      aria-label={`Supprimer · ${repere}`}
                    >
                      Supprimer
                    </Bouton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="border-t border-[var(--color-border)] px-3.5 py-2 print:hidden">
        <Bouton
          variante="ghost"
          taille="sm"
          icone={<IconePlus taille={14} />}
          onClick={surAjoutLigne}
          className="border border-dashed border-[var(--color-border-strong)]"
        >
          Ajouter une ligne
        </Bouton>
      </div>
    </div>
  );
}

/**
 * Le champ « Objectif libre… » et son bouton « + », dans la case.
 *
 * Reprend `_addObjectifLibre` de l'original : le clic sur +, la touche Entrée,
 * ou simplement quitter le champ ajoutent l'objectif. Maj+Entrée passe à la
 * ligne. Le texte est prélevé et le champ vidé AVANT l'envoi, pour qu'un double
 * déclenchement — quitter le champ en cliquant sur + — ne crée pas de doublon ;
 * en cas d'échec, la saisie est rendue.
 */
function ObjectifLibre({
  libelle, surAjout,
}: {
  libelle: string;
  surAjout: (texte: string) => Promise<boolean>;
}) {
  const [texte, setTexte] = useState('');
  const enCours = useRef(false);

  async function ajouter() {
    const propre = texte.trim();
    if (!propre || enCours.current) return;
    enCours.current = true;
    setTexte('');
    try {
      if (!(await surAjout(propre))) setTexte(propre);
    } finally {
      enCours.current = false;
    }
  }

  return (
    <div className="flex gap-1">
      <textarea
        rows={1}
        value={texte}
        aria-label={libelle}
        placeholder="Objectif libre…"
        onChange={(e) => setTexte(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void ajouter();
          }
        }}
        onBlur={() => void ajouter()}
        className="field-sizing-content min-w-0 flex-1 resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-surface-input)] px-1.5 py-1 text-[12px] leading-snug placeholder:text-[var(--color-fg-subtle)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
      />
      <Bouton
        variante="ghost"
        taille="sm"
        iconeSeule
        icone={<IconePlus taille={13} />}
        // `onMouseDown` empêché : sans cela, le clic ôte d'abord le focus du
        // champ, qui ajoute, puis le clic ajoute une seconde fois — vide.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => void ajouter()}
        aria-label="Ajouter cet objectif"
        className="shrink-0 border border-[var(--color-border)]"
      >
        Ajouter cet objectif
      </Bouton>
    </div>
  );
}
